/**
 * App root wrapper - web.
 *
 * The browser build has to mount two providers that Android gets for free:
 *
 *   GestureHandlerRootView - there is no native activity to install the
 *     gesture root, and without it the drawer navigator's swipe handling and
 *     the sell screen's resizable split pane receive no events.
 *
 *   SafeAreaProvider - the web build of react-native-safe-area-context resolves
 *     insets from React context and throws "No safe area value available" if a
 *     SafeAreaView is rendered without a provider above it. App.tsx renders a
 *     SafeAreaView above its own SafeAreaProvider, which native tolerates and
 *     web does not. Nested providers are supported by the library, so the inner
 *     one keeps working unchanged.
 *
 * It also enforces the smallest window the web POS supports (see
 * MinWindowGuard below).
 */

import React, {ReactNode, useEffect, useRef} from 'react';
import {Image, StyleSheet, Text, useWindowDimensions, View} from 'react-native';
import {GestureHandlerRootView} from 'react-native-gesture-handler';
import {SafeAreaProvider} from 'react-native-safe-area-context';

import {webColor, webFontFamily, webFontSize, webSpacing} from '../design/webTokens';

/**
 * Narrowest window the POS layout supports: a landscape tablet. Same limit
 * the Electron build sets on its window (electron/main.js, MIN_WINDOW). A
 * browser cannot be stopped from resizing, so below this width the app is
 * covered with a notice instead of falling back to its tablet-portrait /
 * mobile layouts.
 */
const MIN_WINDOW_WIDTH = 1024;

/**
 * Sits above the app while the window is too narrow.
 *
 * The app stays mounted underneath - an in-progress sale, a half-filled form
 * or an open session survive a window that is dragged small and back - and is
 * made `inert` so keyboard focus cannot reach fields the user cannot see.
 */
const MinWindowGuard = ({children}: {children: ReactNode}) => {
  const {width} = useWindowDimensions();
  const tooSmall = width < MIN_WINDOW_WIDTH;
  const appRef = useRef<View>(null);

  useEffect(() => {
    // react-native-web hands back the DOM node for a View ref.
    const node = appRef.current as unknown as HTMLElement | null;
    if (!node) {
      return;
    }
    if (tooSmall) {
      node.setAttribute('inert', '');
    } else {
      node.removeAttribute('inert');
    }
  }, [tooSmall]);

  return (
    <View style={styles.root}>
      <View ref={appRef} style={styles.root}>
        {children}
      </View>
      {tooSmall && (
        <View style={styles.notice} accessibilityRole="alert">
          <Image
            source={require('../../assets/splash/zenbasket_pos_logo.png')}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel="ZenBasket POS"
          />
          <Text style={styles.title}>Please enlarge your window</Text>
          <Text style={styles.message}>
            ZenBasket POS needs a screen at least {MIN_WINDOW_WIDTH}px wide.
            Maximize the browser window, or turn your tablet to landscape.
          </Text>
        </View>
      )}
    </View>
  );
};

export const AppRoot = ({children}: {children: ReactNode}) => (
  <GestureHandlerRootView style={styles.root}>
    <SafeAreaProvider style={styles.root}>
      <MinWindowGuard>{children}</MinWindowGuard>
    </SafeAreaProvider>
  </GestureHandlerRootView>
);

const styles = StyleSheet.create({
  root: {flex: 1},
  notice: {
    ...StyleSheet.absoluteFillObject,
    // Above modals, popovers and toasts; below the startup splash in index.html.
    zIndex: 2147482000,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: webSpacing.xxl,
    // Same background as the startup splash and the Android bootsplash.
    backgroundColor: '#f5fcff',
  },
  logo: {
    width: 180,
    maxWidth: '50%',
    aspectRatio: 1000 / 751,
    marginBottom: webSpacing.xxl,
  },
  title: {
    fontFamily: webFontFamily.bold,
    fontSize: webFontSize.pageTitle,
    color: webColor.primary,
    textAlign: 'center',
    marginBottom: webSpacing.sm,
  },
  message: {
    fontFamily: webFontFamily.regular,
    fontSize: webFontSize.body,
    lineHeight: 22,
    color: webColor.text,
    textAlign: 'center',
    maxWidth: 420,
  },
});

export default AppRoot;
